import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "../../../components/Select";
import { useToast } from "../../../components/Toast";
import { useAuth } from "../../../auth/AuthContext";
import { localizedName } from "../../../lib/localizedName";
import { formatDate } from "../../../lib/format";
import type { ApiError } from "../../../api/client";
import {
  externalAnalysisApi,
  type ExternalAnalysis,
  type ExternalLaboratory,
  type ExternalPurpose,
  type ExternalValueInput,
} from "../../../api/externalAnalyses";
import { qualityApi, qualityProductApi, type ProductPick, type QualityParameter } from "../../../api/quality";
import { traceabilityApi, type LotHit } from "../../../api/traceability";
import { lotsApi, type ReceivingLot } from "../../../api/lots";
import { deliveriesApi, type Delivery } from "../../../api/deliveries";

/** Na šta se nalaz vezuje. Baza dopušta tačno jednu vezu, pa je i ekran bira kao jednu. */
type LinkKind = "GOODS_LOT" | "RECEIVING_LOT" | "DELIVERY" | "PRODUCT";

const MAX_BYTES = 25 * 1024 * 1024;
const ALLOWED = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

/**
 * Prijava nalaza spoljne laboratorije.
 *
 * Nalaz je i dokument i merenje. Sam PDF ne odgovara ni na jedno pitanje koje se posle postavlja — koliki
 * je bio rezultat, da li je prošao, kako se kreće kroz godinu — pa se vrednosti unose uz fajl. U njihovom
 * programu mikrobiologija je 2025. naručena 1.396 puta i izmerena nijedan put: nalaz je stizao spolja i
 * nigde se nije upisivao.
 *
 * Fajl ide u tri koraka — potpis, pretraživač ga šalje pravo u skladište, pa potvrda — pa se izveštaj prvo
 * sačuva bez fajla. Tako unos ne propada ako skladište zakaže; nalaz već stoji, fajl se dodaje posle.
 */
export default function ExternalAnalysisForm({
  laboratories,
  onSaved,
  onCancel,
}: {
  laboratories: ExternalLaboratory[];
  onSaved: (a: ExternalAnalysis) => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasRole, hasPermission } = useAuth();
  const lang = i18n.resolvedLanguage ?? "sr";

  // Nalaz koji kupac vidi pušta direktor kvaliteta. Isto pravilo stoji i na serveru, u create i u update;
  // ovde se opcija ne nudi da se ne traži ono što će biti odbijeno.
  const smePuštatiKupcu = hasPermission("section:external_analyses:full") && (hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("KVALITET_LEAD"));

  const [laboratoryId, setLaboratoryId] = useState(laboratories[0]?.id ?? "");
  const [subject, setSubject] = useState("");
  const [reportNumber, setReportNumber] = useState("");
  const [issuedOn, setIssuedOn] = useState("");
  const [purpose, setPurpose] = useState<ExternalPurpose>("INTERNAL");
  const [note, setNote] = useState("");

  const [linkKind, setLinkKind] = useState<LinkKind>("GOODS_LOT");
  const [lotTerm, setLotTerm] = useState("");
  const [lotHits, setLotHits] = useState<LotHit[] | null>(null);
  const [goodsLot, setGoodsLot] = useState<LotHit | null>(null);
  const [receivingLots, setReceivingLots] = useState<ReceivingLot[]>([]);
  const [receivingLotId, setReceivingLotId] = useState("");
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [deliveryId, setDeliveryId] = useState("");
  const [productTerm, setProductTerm] = useState("");
  const [productHits, setProductHits] = useState<ProductPick[] | null>(null);
  const [product, setProduct] = useState<ProductPick | null>(null);

  const [parameters, setParameters] = useState<QualityParameter[]>([]);
  const [rows, setRows] = useState<{ parameterId: string; value: string; note: string }[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void qualityApi
      .listParameters(true)
      .then(setParameters)
      .catch(() => setParameters([]));
  }, []);

  useEffect(() => {
    if (linkKind === "RECEIVING_LOT" && receivingLots.length === 0) {
      void lotsApi
        .list()
        .then(setReceivingLots)
        .catch(() => setReceivingLots([]));
    }
    if (linkKind === "DELIVERY" && deliveries.length === 0) {
      // Birač isporuke uz spoljni nalaz: jedna strana najnovijih, ne ceo registar.
      void deliveriesApi
        .list({}, 0, 200)
        .then((r) => setDeliveries(r.content))
        .catch(() => setDeliveries([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkKind]);

  function pickedLink(): { field: string; id: string } | null {
    if (linkKind === "GOODS_LOT") return goodsLot ? { field: "goodsLotId", id: goodsLot.id } : null;
    if (linkKind === "RECEIVING_LOT") return receivingLotId ? { field: "receivingLotId", id: receivingLotId } : null;
    if (linkKind === "DELIVERY") return deliveryId ? { field: "deliveryId", id: deliveryId } : null;
    return product ? { field: "productId", id: product.id } : null;
  }

  function parameterOf(id: string): QualityParameter | undefined {
    return parameters.find((p) => p.id === id);
  }

  /** Vrednost se čita po tipu rezultata parametra: broj, da/ne, ili tekst. Prazan red se ne šalje. */
  function valuesToSend(): ExternalValueInput[] {
    const out: ExternalValueInput[] = [];
    for (const r of rows) {
      if (!r.parameterId) continue;
      const p = parameterOf(r.parameterId);
      const raw = r.value.trim();
      if (!raw && !r.note.trim()) continue;
      const v: ExternalValueInput = { parameterId: r.parameterId, note: r.note.trim() || null };
      if (p?.resultType === "BOOLEAN") {
        v.valueBool = raw === "true";
      } else if (p?.resultType === "TEXT") {
        v.valueText = raw || null;
      } else {
        const n = Number(raw.replace(",", "."));
        if (raw && !Number.isNaN(n)) v.valueNum = n;
        else if (raw) v.valueText = raw;
      }
      out.push(v);
    }
    return out;
  }

  async function uploadFile(id: string) {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(t("external.fileTooBig", "Fajl je veći od 25 MB."));
      return;
    }
    if (!ALLOWED.includes(file.type)) {
      toast.error(t("external.fileType", "Dozvoljeni su PDF, JPEG, PNG i WebP."));
      return;
    }
    const signed = await externalAnalysisApi.presign(id, {
      fileName: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    });
    const put = await fetch(signed.url, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!put.ok) throw new Error(t("external.uploadFailed", "Skladište nije primilo fajl."));
    await externalAnalysisApi.confirm(id, {
      objectKey: signed.objectKey,
      fileName: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const link = pickedLink();
    if (!laboratoryId) {
      toast.error(t("external.needLab", "Izaberite laboratoriju."));
      return;
    }
    if (!subject.trim()) {
      toast.error(t("external.needSubject", "Unesite predmet analize."));
      return;
    }
    if (!link) {
      toast.error(t("external.needLink", "Izaberite na šta se nalaz odnosi."));
      return;
    }
    setSaving(true);
    try {
      const created = await externalAnalysisApi.create({
        laboratoryId,
        subject: subject.trim(),
        reportNumber: reportNumber.trim() || null,
        issuedOn: issuedOn || null,
        purpose,
        note: note.trim() || null,
        values: valuesToSend(),
        [link.field]: link.id,
      });
      // Fajl ne odlucuje o nalazu: nalaz je sacuvan, pa se fajl dodaje. Ako skladiste zakaze, red ostaje.
      try {
        await uploadFile(created.id);
      } catch (e) {
        toast.error((e as ApiError).message ?? String(e));
        onSaved(created);
        return;
      }
      toast.success(t("external.saved", "Nalaz je prijavljen."));
      onSaved(created);
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setSaving(false);
    }
  }

  const purposeOptions: { value: ExternalPurpose; label: string }[] = [
    { value: "INTERNAL", label: t("external.purpose.INTERNAL", "interno") },
    ...(smePuštatiKupcu
      ? [{ value: "FOR_CUSTOMER" as ExternalPurpose, label: t("external.purpose.FOR_CUSTOMER", "za kupca") }]
      : []),
    { value: "DISPUTE", label: t("external.purpose.DISPUTE", "superanaliza") },
  ];

  return (
    <form className="card" onSubmit={submit}>
      <div className="card-h">
        <strong>{t("external.newTitle", "Nalaz spoljne laboratorije")}</strong>
        <span className="muted">
          {t("external.newLead", "Fajl i vrednosti iz njega. Bez vrednosti nalaz se posle ne može ni pretražiti ni nacrtati.")}
        </span>
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <label>
            <span className="muted">{t("external.laboratory", "Laboratorija")}</span>
            <Select
              value={laboratoryId}
              onChange={setLaboratoryId}
              placeholder={t("external.pickLab", "izaberite")}
              options={laboratories.map((l) => ({
                value: l.id,
                label: l.accreditation ? `${l.name} (${l.accreditation})` : l.name,
              }))}
            />
          </label>
          <label>
            <span className="muted">{t("external.subject", "Predmet")}</span>
            <input
              className="input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("external.subjectHint", "na primer: mikrobiologija, sojina sačma")}
            />
          </label>
          <label>
            <span className="muted">{t("external.reportNumber", "Broj izveštaja")}</span>
            <input className="input" value={reportNumber} onChange={(e) => setReportNumber(e.target.value)} />
          </label>
          <label>
            <span className="muted">{t("external.issuedOn", "Datum izveštaja")}</span>
            <input className="input" type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
          </label>
          <label>
            <span className="muted">{t("external.purposeLabel", "Namena")}</span>
            <Select value={purpose} onChange={(v) => setPurpose(v)} options={purposeOptions} />
          </label>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span className="muted">{t("external.link", "Nalaz se odnosi na")}</span>
          <div className="seg" role="group">
            {(
              [
                ["GOODS_LOT", t("external.link.goodsLot", "LOT gotove robe")],
                ["RECEIVING_LOT", t("external.link.receivingLot", "prijemna šarža")],
                ["DELIVERY", t("external.link.delivery", "isporuka")],
                ["PRODUCT", t("external.link.product", "proizvod")],
              ] as [LinkKind, string][]
            ).map(([k, label]) => (
              <button key={k} type="button" aria-pressed={linkKind === k} onClick={() => setLinkKind(k)}>
                {label}
              </button>
            ))}
          </div>

          {linkKind === "GOODS_LOT" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div className="toolbar">
                <input
                  className="input"
                  value={lotTerm}
                  onChange={(e) => setLotTerm(e.target.value)}
                  placeholder={t("external.lotSearch", "oznaka LOT-a")}
                />
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => {
                    void traceabilityApi
                      .search(lotTerm)
                      .then((r) => setLotHits(r.rows))
                      .catch(() => setLotHits([]));
                  }}
                >
                  {t("common.search", "Traži")}
                </button>
              </div>
              {goodsLot ? (
                <span className="tag neutral">
                  {goodsLot.designation ?? goodsLot.id} — {goodsLot.productName ?? ""}
                </span>
              ) : (
                lotHits !== null && (
                  <div className="cat-list">
                    {lotHits.length === 0 && <div className="empty">{t("external.noLot", "Nema LOT-a po toj oznaci.")}</div>}
                    {lotHits.slice(0, 20).map((h) => (
                      <button key={h.id} type="button" className="btn ghost sm" onClick={() => setGoodsLot(h)}>
                        {h.designation ?? h.id} — {h.productName ?? ""}
                      </button>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {linkKind === "RECEIVING_LOT" && (
            <Select
              value={receivingLotId}
              onChange={setReceivingLotId}
              placeholder={t("external.pickReceivingLot", "izaberite šaržu")}
              options={receivingLots.map((l) => ({
                value: l.id,
                label: `${l.lotNumber} — ${l.culture} — ${formatDate(l.receivedOn, lang)}`,
              }))}
            />
          )}

          {linkKind === "DELIVERY" && (
            <Select
              value={deliveryId}
              onChange={setDeliveryId}
              placeholder={t("external.pickDelivery", "izaberite isporuku")}
              options={deliveries.map((d) => ({
                value: d.id,
                label: `${formatDate(d.announcedDate, lang)} — ${d.companyName}${d.vehiclePlate ? ` — ${d.vehiclePlate}` : ""}`,
              }))}
            />
          )}

          {linkKind === "PRODUCT" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div className="toolbar">
                <input
                  className="input"
                  value={productTerm}
                  onChange={(e) => setProductTerm(e.target.value)}
                  placeholder={t("external.productSearch", "šifra ili naziv proizvoda")}
                />
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => {
                    void qualityProductApi
                      .search(productTerm, lang)
                      .then(setProductHits)
                      .catch(() => setProductHits([]));
                  }}
                >
                  {t("common.search", "Traži")}
                </button>
              </div>
              {product ? (
                <span className="tag neutral">
                  {product.sku} — {product.name}
                </span>
              ) : (
                productHits !== null && (
                  <div className="cat-list">
                    {productHits.length === 0 && (
                      <div className="empty">{t("external.noProduct", "Nema proizvoda po tom upitu.")}</div>
                    )}
                    {productHits.map((p) => (
                      <button key={p.id} type="button" className="btn ghost sm" onClick={() => setProduct(p)}>
                        {p.sku} — {p.name}
                      </button>
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span className="muted">{t("external.values", "Vrednosti iz nalaza")}</span>
          {rows.length === 0 && (
            <div className="empty">
              {t("external.noValues", "Nijedna vrednost nije uneta. Nalaz će ostati samo fajl.")}
            </div>
          )}
          {rows.map((r, i) => {
            const p = parameterOf(r.parameterId);
            return (
              <div key={i} className="toolbar" style={{ gap: 8 }}>
                <Select
                  value={r.parameterId}
                  onChange={(v) => setRows(rows.map((x, j) => (j === i ? { ...x, parameterId: v } : x)))}
                  placeholder={t("external.pickParameter", "parametar")}
                  options={parameters.map((q) => ({
                    value: q.id,
                    label:
                      localizedName({ name: q.key, nameI18n: q.nameI18n }, lang) +
                      (q.unit ? ` (${q.unit})` : ""),
                  }))}
                />
                {p?.resultType === "BOOLEAN" ? (
                  <Select
                    value={r.value}
                    onChange={(v) => setRows(rows.map((x, j) => (j === i ? { ...x, value: v } : x)))}
                    placeholder={t("external.pickBool", "da/ne")}
                    options={[
                      { value: "true", label: t("common.yes", "da") },
                      { value: "false", label: t("common.no", "ne") },
                    ]}
                  />
                ) : (
                  <input
                    className="input"
                    value={r.value}
                    onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                    placeholder={p?.unit || t("external.value", "vrednost")}
                  />
                )}
                <input
                  className="input"
                  value={r.note}
                  onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))}
                  placeholder={t("external.valueNote", "napomena")}
                />
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}
                  aria-label={t("common.remove", "Izbaci")}
                >
                  ×
                </button>
              </div>
            );
          })}
          <button
            type="button"
            className="btn sm"
            onClick={() => setRows([...rows, { parameterId: "", value: "", note: "" }])}
          >
            {t("external.addValue", "Dodaj vrednost")}
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          <label>
            <span className="muted">{t("external.file", "Nalaz (PDF ili slika)")}</span>
            <input
              className="input"
              type="file"
              accept={ALLOWED.join(",")}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <label>
            <span className="muted">{t("external.note", "Napomena")}</span>
            <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>

        <div className="page-actions">
          <button type="submit" className="btn primary" disabled={saving}>
            {saving ? t("common.saving", "Čuvam…") : t("external.save", "Prijavi nalaz")}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel} disabled={saving}>
            {t("common.cancel", "Odustani")}
          </button>
        </div>
      </div>
    </form>
  );
}
